"use client";

import { ArrowRight } from "lucide-react";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { AvatarStack } from "@/components/ui/avatar";
import { useConfigLocalizer } from "@/lib/localize-config-text";
import type { HomeBlock, WidgetConfig, WidgetTab } from "@/lib/widget-config";

export function AskQuestionBlock({
  block,
  config,
  onOpenTab,
}: {
  block: Extract<HomeBlock, { kind: "ask-question" }>;
  config: WidgetConfig;
  onOpenTab: (tab: WidgetTab) => void;
}) {
  const t = useTranslations("home");
  const localize = useConfigLocalizer();
  const target = block.action ?? "open-messenger";
  const tab: WidgetTab =
    target === "open-help" ? "help" : target === "open-news" ? "news" : "messages";
  return (
    <motion.button
      type="button"
      onClick={() => onOpenTab(tab)}
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.99 }}
      transition={{ type: "spring", stiffness: 360, damping: 26 }}
      className="flex w-full items-center gap-3 rounded-wx-lg bg-wx-bg-elevated px-4 py-3.5 text-start transition-colors hover:bg-wx-bg-elevated-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-wx-primary"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-base font-semibold text-wx-fg">
          {localize(block.title, t("askTitle"))}
        </p>
        <p className="mt-0.5 truncate text-sm text-wx-fg-muted">
          {localize(block.subtitle, t("askSubtitle"))}
        </p>
      </div>
      {config.operatorAvatars.length > 0 && (
        <AvatarStack avatars={config.operatorAvatars} size="sm" ringClass="ring-wx-bg-elevated" />
      )}
      <ArrowRight size={16} className="shrink-0 text-wx-fg-muted rtl:-scale-x-100" />
    </motion.button>
  );
}
